import { useEffect, useMemo, useState } from "react";

export type ScreenType = "mobile" | "desktop";

function isMobileUserAgent(userAgent: string): boolean {
  return /Android|iPhone|iPad|iPod/i.test(userAgent);
}

function getScreenTypeNow(): ScreenType {
  const isSmallScreen = window.matchMedia("(max-width: 767px)").matches;
  const isMobileUA = isMobileUserAgent(navigator.userAgent);
  return isSmallScreen && isMobileUA ? "mobile" : "desktop";
}

/**
 * SSR-safe screen classification used for routing decisions.
 *
 * Returns `null` until mounted (so callers can avoid hydration flicker / redirects on the server).
 */
export function useScreenType(): ScreenType | null {
  const [value, setValue] = useState<ScreenType | null>(null);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");

    const update = () => setValue(getScreenTypeNow());
    update();

    // Safari < 14 uses addListener/removeListener
    const anyMq = mq as unknown as {
      addEventListener?: (type: "change", listener: () => void) => void;
      removeEventListener?: (type: "change", listener: () => void) => void;
      addListener?: (listener: () => void) => void;
      removeListener?: (listener: () => void) => void;
    };

    if (anyMq.addEventListener && anyMq.removeEventListener) {
      anyMq.addEventListener("change", update);
      return () => anyMq.removeEventListener?.("change", update);
    }

    anyMq.addListener?.(update);
    return () => anyMq.removeListener?.(update);
  }, []);

  // Ensure stable reference type-wise (string literals) for callers.
  return useMemo(() => value, [value]);
}

